import { useEffect, useState } from "react";

import {
  type ApiDeps,
  type SovereigntyView,
  fetchSovereignty,
} from "../lib/api";
import { t, tn, type Language, type StringKey } from "../lib/i18n";
import { Icon } from "./icons";

interface CapabilitySummaryProps {
  deps: ApiDeps;
  language: Language;
  /** Rolling window length threaded from the RangePicker (the sovereignty
   * projection is range-aware). */
  periodDays: number;
  /** Shared refresh signal — bump to refetch. */
  reloadKey?: number;
  /** Navigate to the Trust tab ("Ver tudo →"). */
  onOpenTrust: () => void;
}

const MAX_SCOPE_PARTS = 4;

/** Friendly label for one scope-count key, falling back to the raw key when
 * the SPA has no string for it yet (mirrors the sovereignty pane). */
function scopeLabel(key: string, language: Language): string {
  const i18nKey = `sovereignty.scope.${key}` as StringKey;
  try {
    return t(i18nKey, language);
  } catch {
    return key;
  }
}

/** One-glance capability strip on the Overview (§12.6, Task 7) — promotes
 * the "what can the agent do, and where" story out of the Trust tab. Reads
 * the existing `sovereignty_view` projection (enabled-axis count + scope
 * counts) and links through to the full Trust tab. Degrades to rendering
 * nothing while loading / on error so it never blocks the Overview.
 * Universal — no tenant copy. */
export function CapabilitySummary({
  deps,
  language,
  periodDays,
  reloadKey = 0,
  onOpenTrust,
}: CapabilitySummaryProps): JSX.Element | null {
  const [view, setView] = useState<SovereigntyView | null>(null);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    fetchSovereignty(deps, periodDays)
      .then((v) => {
        if (!cancelled) setView(v);
      })
      .catch(() => {
        /* a strip-only failure degrades to nothing; Trust tab still works */
      });
    return () => {
      cancelled = true;
    };
  }, [deps.backendUrl, deps.getAuthToken, periodDays, reloadKey]);

  if (view === null) return null;

  const enabled = view.axes.filter((a) => a.enabled).length;
  const total = view.axes.length;
  // "Active on X of Y" is a permission count; on a window with no activity
  // behind it, it overstates (design review finding 10). The projection
  // already carries the per-axis count, so the zero case is free.
  const active = view.axes.some((a) => a.count_30d > 0);
  const scope = Object.entries(view.scope_counts)
    .filter(([, n]) => n > 0)
    .slice(0, MAX_SCOPE_PARTS)
    .map(([key, n]) => `${n} ${scopeLabel(key, language)}`)
    .join(" · ");

  return (
    <button
      type="button"
      onClick={onOpenTrust}
      data-testid="capability-summary"
      className="flex w-full items-center gap-2 rounded-[2px] border border-kwanza-divider bg-kwanza-surface px-4 py-2.5 text-left text-sm hover:border-kwanza-accent"
    >
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[2px] bg-kwanza-accentSoft text-kwanza-accentText"
        aria-hidden="true"
      >
        <Icon name="shield-check" size={15} />
      </span>
      <span className="min-w-0 flex-1 text-kwanza-ink">
        {tn(
          active ? "capsum.summary" : "capsum.idle",
          { x: enabled, y: total },
          language,
        )}
        {scope ? (
          <span className="text-kwanza-inkSoft">
            {" · "}
            {tn("capsum.scope", { scope }, language)}
          </span>
        ) : null}
      </span>
      <span className="inline-flex shrink-0 items-center gap-1 font-medium text-kwanza-accentText">
        {t("capsum.link", language)}
        <Icon name="arrow-right" size={14} />
      </span>
    </button>
  );
}
